import { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { 
  ArrowLeft, Zap, Flame, CheckCircle, XCircle, TrendingDown, AlertTriangle, Lightbulb,
  ArrowRight, Euro, Calendar, Building2, Home, Star, Gift, Clock, Shield, Globe
} from "lucide-react";
import OffersBanner from "../components/OffersBanner";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ad8cae97-e844-4242-bf8a-3cc50b57fa19/artifacts/vj8eyvuz_logo%20%2B%20personaggio%20-%208energy%20srl%20%20%281%29.svg";
const OTTO_URL = "https://static.wixstatic.com/media/709795_dcb4777a39394c9fb86ba6ad9678de09~mv2.png";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AnalysisResult() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isGuest, setIsGuest] = useState(false);

  useEffect(() => { fetchAnalysis(); }, [id]);

  const fetchAnalysis = async () => {
    try {
      // Check if we have a guest token for this analysis
      const guestToken = localStorage.getItem(`guest_analysis_${id}`);
      
      let url = `${API}/bills/analyses/${id}`;
      if (guestToken) {
        url += `?guest_token=${guestToken}`;
      }
      
      const response = await fetch(url, { credentials: "include" });
      if (!response.ok) throw new Error("Analisi non trovata");
      
      const data = await response.json();
      setAnalysis(data);
      setIsGuest(data.is_guest === true);
    } catch (error) {
      console.error("Error:", error);
      // Show error page instead of redirecting
      setAnalysis(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="w-10 h-10 border-3 border-[#006838] border-t-transparent rounded-full animate-spin"></div>
    </div>
  );
  
  if (!analysis) return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Analisi non trovata</h2>
        <p className="text-gray-500 mb-6">L'analisi richiesta non esiste o è stata rimossa.</p>
        <Link to="/upload">
          <Button className="bg-[#006838] hover:bg-[#005530] text-white rounded-full">
            Carica una nuova bolletta
          </Button>
        </Link>
      </div>
    </div>
  );

  const isPassed = analysis.rating === "promossa";
  const billData = analysis.bill_data || {};
  const isBusinesss = analysis.tipo_utenza === "business" || billData.tipo_utenza === "business" || billData.cliente_business;
  const offerte = analysis.offerte_consigliate || [];

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }} data-testid="analysis-result-page">
      {/* Navigation */}
      <nav className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center justify-between h-16">
            <Link to="/dashboard"><img src={LOGO_URL} alt="8energy" className="h-8" /></Link>
            <Link to="/dashboard">
              <Button variant="ghost" className="rounded-full text-gray-500 hover:text-gray-900">
                <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />Dashboard
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      <main className="max-w-5xl mx-auto px-6 py-10">
        {/* Result Header Card */}
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="mb-10">
          <div className={`rounded-[2rem] p-8 relative overflow-hidden ${isPassed ? "bg-green-50 border border-green-200" : "bg-red-50 border border-red-200"}`}>
            {/* Background OTTO */}
            <div className="absolute -right-10 -bottom-10 opacity-10">
              <img src={OTTO_URL} alt="" className="w-48" />
            </div>
            
            <div className="flex flex-col md:flex-row items-center gap-8 relative z-10">
              {/* Score Circle */}
              <div className="relative">
                <div className={`w-36 h-36 rounded-full flex items-center justify-center ${isPassed ? "bg-green-100" : "bg-red-100"}`}>
                  <div className="text-center">
                    <span className={`text-5xl font-bold ${isPassed ? "text-green-600" : "text-red-500"}`}>{analysis.score}</span>
                    <span className={`text-sm block ${isPassed ? "text-green-600" : "text-red-500"}`}>/100</span>
                  </div>
                </div>
                <div className={`absolute -bottom-3 left-1/2 -translate-x-1/2 px-5 py-1.5 rounded-full text-sm font-bold text-white ${isPassed ? "bg-green-500" : "bg-red-500"}`}>
                  {isPassed ? "PROMOSSA" : "BOCCIATA"}
                </div>
              </div>

              {/* Info */}
              <div className="flex-1 text-center md:text-left">
                <div className="flex items-center justify-center md:justify-start gap-3 mb-3">
                  <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${billData.tipo === "luce" ? "bg-amber-100" : "bg-orange-100"}`}>
                    {billData.tipo === "luce" ? <Zap className="w-6 h-6 text-amber-500" strokeWidth={1.5} /> : <Flame className="w-6 h-6 text-orange-500" strokeWidth={1.5} />}
                  </div>
                  <h1 className="text-2xl font-bold text-gray-900">Bolletta {billData.tipo === "luce" ? "Luce" : "Gas"}</h1>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${isBusinesss ? "bg-blue-100 text-blue-700" : "bg-purple-100 text-purple-700"}`}>
                    {isBusinesss ? "Business" : "Domestico"}
                  </span>
                </div>
                <p className={`text-lg ${isPassed ? "text-green-700" : "text-red-600"}`}>{analysis.spiegazione}</p>
              </div>

              {/* Savings */}
              {analysis.risparmio_con_8energy > 0 && (
                <div className="bg-white rounded-2xl p-5 text-center shadow-sm">
                  <p className="text-sm text-gray-500 mb-1">Con 8energy risparmi</p>
                  <p className="text-4xl font-bold text-[#006838]">€{analysis.risparmio_con_8energy.toFixed(0)}</p>
                  <p className="text-sm text-[#006838]/70">al mese</p>
                </div>
              )}
            </div>
          </div>
        </motion.div>

        {/* CONFRONTO BOLLETTE - Side by Side */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="mb-10">
          <h2 className="text-xl font-bold text-gray-900 mb-5 flex items-center gap-2">
            <Euro className="w-5 h-5 text-[#006838]" strokeWidth={1.5} />
            Confronto bollette
          </h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* SINISTRA - Bolletta Attuale */}
            <div className="relative">
              <div className="absolute -top-3 left-4 z-10">
                <span className="bg-red-500 text-white px-4 py-1.5 rounded-full text-xs font-bold shadow-lg">
                  LA TUA BOLLETTA
                </span>
              </div>
              <div className="bg-gradient-to-br from-red-50 to-red-100/50 border-2 border-red-200 rounded-[2rem] p-6 pt-8 h-full">
                {/* Bill Header */}
                <div className="flex items-center justify-between mb-6 pb-4 border-b border-red-200">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${billData.tipo === "luce" ? "bg-amber-100" : "bg-orange-100"}`}>
                      {billData.tipo === "luce" ? <Zap className="w-6 h-6 text-amber-500" strokeWidth={1.5} /> : <Flame className="w-6 h-6 text-orange-500" strokeWidth={1.5} />}
                    </div>
                    <div>
                      <p className="font-bold text-gray-900">{billData.fornitore || "Fornitore attuale"}</p>
                      <p className="text-sm text-gray-500">{billData.periodo_fatturazione || "Periodo corrente"}</p>
                    </div>
                  </div>
                  <XCircle className="w-8 h-8 text-red-400" strokeWidth={1.5} />
                </div>
                
                {/* Bill Details */}
                <div className="space-y-4 mb-6">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Consumo</span>
                    <span className="font-bold text-gray-900">
                      {billData.tipo === "luce" ? `${billData.consumo_kwh || 0} kWh` : `${billData.consumo_smc || 0} Smc`}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Prezzo energia</span>
                    <span className="font-bold text-red-600">
                      €{billData.tipo === "luce" ? (billData.prezzo_kwh?.toFixed(4) || "N/D") : (billData.prezzo_smc?.toFixed(4) || "N/D")}/{billData.tipo === "luce" ? "kWh" : "Smc"}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Costi fissi</span>
                    <span className="font-bold text-gray-900">Inclusi</span>
                  </div>
                </div>
                
                {/* Total */}
                <div className="bg-red-100 rounded-2xl p-5 text-center">
                  <p className="text-sm text-red-600 mb-1">TOTALE BOLLETTA</p>
                  <p className="text-4xl font-black text-red-600">
                    €{(billData.totale_bolletta || billData.costo_totale || analysis.costo_attuale_mensile || 0).toFixed(2)}
                  </p>
                  <p className="text-xs text-red-500 mt-1">
                    {analysis.costo_attuale_mensile ? `≈ €${analysis.costo_attuale_mensile.toFixed(2)}/mese` : ""}
                  </p>
                </div>
              </div>
            </div>

            {/* DESTRA - Simulazione 8energy */}
            <div className="relative">
              <div className="absolute -top-3 left-4 z-10">
                <span className="bg-[#006838] text-white px-4 py-1.5 rounded-full text-xs font-bold shadow-lg flex items-center gap-2">
                  <Star className="w-3 h-3" /> CON 8ENERGY
                </span>
              </div>
              <div className="bg-gradient-to-br from-[#f0fff4] to-[#ADFF2F]/10 border-2 border-[#ADFF2F] rounded-[2rem] p-6 pt-8 h-full relative overflow-hidden">
                {/* Decorative glow */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-[#ADFF2F]/20 rounded-full blur-3xl"></div>
                
                {/* Bill Header */}
                <div className="flex items-center justify-between mb-6 pb-4 border-b border-[#ADFF2F]/50 relative z-10">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-[#006838] flex items-center justify-center">
                      <img src={LOGO_URL} alt="8energy" className="h-6 brightness-0 invert" />
                    </div>
                    <div>
                      <p className="font-bold text-[#006838]">8energy</p>
                      <p className="text-sm text-gray-500">{billData.tipo === "luce" ? "Luce Plus Online" : "Gas Plus Online"}</p>
                    </div>
                  </div>
                  <CheckCircle className="w-8 h-8 text-[#006838]" strokeWidth={1.5} />
                </div>
                
                {/* Bill Details */}
                <div className="space-y-4 mb-6 relative z-10">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Consumo</span>
                    <span className="font-bold text-gray-900">
                      {billData.tipo === "luce" ? `${billData.consumo_kwh || 0} kWh` : `${billData.consumo_smc || 0} Smc`}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Prezzo energia</span>
                    <span className="font-bold text-[#006838]">
                      {billData.tipo === "luce" ? "PUN + 0.013" : "PSV + 0.110"} €/{billData.tipo === "luce" ? "kWh" : "Smc"}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Costi fissi</span>
                    <span className="font-bold text-gray-900">€8.50/mese</span>
                  </div>
                  <div className="flex justify-between items-center bg-[#ADFF2F]/30 rounded-lg px-3 py-2 -mx-3">
                    <span className="text-[#006838] font-semibold flex items-center gap-1">
                      <Gift className="w-4 h-4" /> Bonus Online
                    </span>
                    <span className="font-bold text-[#006838]">Incluso</span>
                  </div>
                </div>
                
                {/* Total */}
                <div className="bg-[#006838] rounded-2xl p-5 text-center relative z-10">
                  <p className="text-sm text-[#ADFF2F] mb-1">TOTALE STIMATO</p>
                  <p className="text-4xl font-black text-white">
                    €{(analysis.costo_8energy_mensile || 0).toFixed(2)}
                  </p>
                  <p className="text-xs text-[#ADFF2F] mt-1">/mese</p>
                </div>
                
                {/* Savings Banner */}
                {analysis.risparmio_con_8energy > 0 && (
                  <div className="mt-4 bg-white border-2 border-[#ADFF2F] rounded-xl p-4 text-center relative z-10">
                    <p className="text-sm text-gray-600">RISPARMIO STIMATO</p>
                    <div className="flex items-center justify-center gap-4 mt-2">
                      <div>
                        <p className="text-2xl font-black text-[#006838]">€{analysis.risparmio_con_8energy.toFixed(0)}</p>
                        <p className="text-xs text-gray-500">/mese</p>
                      </div>
                      <div className="w-px h-10 bg-gray-200"></div>
                      <div>
                        <p className="text-2xl font-black text-[#006838]">€{(analysis.risparmio_annuale || analysis.risparmio_con_8energy * 12).toFixed(0)}</p>
                        <p className="text-xs text-gray-500">/anno</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Bill Details */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="mb-10">
          <h2 className="text-xl font-bold text-gray-900 mb-5">Dettagli bolletta</h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {[
              { icon: Building2, label: "Fornitore", value: billData.fornitore || "N/D" },
              { icon: Calendar, label: "Periodo", value: billData.periodo_fatturazione || "N/D" },
              { icon: billData.tipo === "luce" ? Zap : Flame, label: "Consumo", value: billData.tipo === "luce" ? `${billData.consumo_kwh || 0} kWh` : `${billData.consumo_smc || 0} Smc` },
              { icon: Euro, label: "Totale", value: `€${billData.costo_totale?.toFixed(2) || "N/D"}` },
              { icon: TrendingDown, label: "Prezzo", value: billData.tipo === "luce" ? `€${billData.prezzo_kwh?.toFixed(3) || "N/D"}/kWh` : `€${billData.prezzo_smc?.toFixed(3) || "N/D"}/Smc`, highlight: !isPassed }
            ].map((item, i) => (
              <div key={i} className="bg-[#f5f5f7] rounded-2xl p-5">
                <div className="flex items-center gap-2 text-gray-500 mb-2">
                  <item.icon className="w-4 h-4" strokeWidth={1.5} />
                  <span className="text-xs font-medium">{item.label}</span>
                </div>
                <p className={`font-semibold ${item.highlight ? (isPassed ? "text-green-600" : "text-red-500") : "text-gray-900"}`}>
                  {item.value}
                </p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Problems & Tips */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
          {analysis.problemi?.length > 0 && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 h-full">
                <div className="flex items-center gap-2 text-amber-700 font-bold mb-4">
                  <AlertTriangle className="w-5 h-5" strokeWidth={1.5} />
                  Problemi identificati
                </div>
                <ul className="space-y-3">
                  {analysis.problemi.map((p, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <XCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                      <span className="text-amber-800 text-sm">{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          )}
          {analysis.suggerimenti_risparmio?.length > 0 && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
              <div className="bg-[#ADFF2F]/20 border border-[#ADFF2F]/50 rounded-2xl p-6 h-full">
                <div className="flex items-center gap-2 text-[#006838] font-bold mb-4">
                  <Lightbulb className="w-5 h-5" strokeWidth={1.5} />
                  Suggerimenti per risparmiare
                </div>
                <ul className="space-y-3">
                  {analysis.suggerimenti_risparmio.map((s, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-[#006838] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                      <span className="text-[#006838] text-sm">{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          )}
        </div>

        {/* Why 8energy */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="mb-10">
          <div className="bg-gray-900 rounded-[2rem] p-8 text-white relative overflow-hidden">
            <div className="absolute -right-10 bottom-0 opacity-20">
              <img src={OTTO_URL} alt="" className="w-56" />
            </div>
            <div className="relative z-10">
              <div className="flex items-center gap-2 mb-6">
                <Star className="w-6 h-6 text-[#ADFF2F]" strokeWidth={1.5} />
                <h2 className="text-2xl font-bold">Perché passare a 8energy?</h2>
              </div>
              
              {analysis.perche_8energy?.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                  {analysis.perche_8energy.map((reason, i) => (
                    <div key={i} className="flex items-start gap-3 bg-white/10 rounded-xl p-4">
                      <CheckCircle className="w-5 h-5 text-[#ADFF2F] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                      <span className="text-white/90">{reason}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="bg-white/10 rounded-2xl p-6 mb-8">
                <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                  <div>
                    <p className="text-white/70 mb-1">Risparmio stimato con 8energy</p>
                    <p className="text-4xl font-bold text-[#ADFF2F]">
                      €{(analysis.risparmio_con_8energy || analysis.risparmio_potenziale || 0).toFixed(0)} 
                      <span className="text-lg font-normal text-white/70"> /mese</span>
                    </p>
                  </div>
                  <div className="flex items-center gap-2 bg-[#ADFF2F] text-[#006838] px-5 py-2 rounded-full text-sm font-bold">
                    <Globe className="w-4 h-4" strokeWidth={1.5} />
                    SWITCH 100% ONLINE
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { icon: Gift, text: "Nessun costo di attivazione" },
                  { icon: Clock, text: "Switch in 5 minuti" },
                  { icon: Shield, text: "Nessuna interruzione" },
                  { icon: Star, text: "Assistenza dedicata" }
                ].map((item, i) => (
                  <div key={i} className="bg-white/10 rounded-xl p-4 text-center">
                    <item.icon className="w-6 h-6 mx-auto mb-2 text-[#ADFF2F]" strokeWidth={1.5} />
                    <p className="text-sm text-white/80">{item.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>

        {/* 8energy Offers Banner */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }} className="mb-10">
          <h2 className="text-xl font-bold text-gray-900 mb-5 flex items-center gap-2">
            <Star className="w-5 h-5 text-[#006838]" strokeWidth={1.5} />
            Le nostre offerte
          </h2>
          <OffersBanner tipo={billData.tipo || "both"} showCta={true} />
        </motion.div>

        {/* Offers */}
        {offerte.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} className="mb-10">
            <h2 className="text-xl font-bold text-gray-900 mb-5 flex items-center gap-2">
              <Star className="w-5 h-5 text-[#006838]" strokeWidth={1.5} />
              Offerte 8energy consigliate
              <span className="text-sm font-normal text-gray-500 ml-2">({isBusinesss ? "Business" : "Domestico"})</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {offerte.map((offer, i) => (
                <div key={offer.offer_id} className={`bg-white rounded-2xl p-6 border-2 transition-all hover:shadow-lg ${i === 0 ? "border-[#006838]" : "border-gray-100"}`}>
                  {i === 0 && (
                    <div className="inline-flex items-center gap-1 bg-[#006838] text-white px-3 py-1 rounded-full text-xs font-bold mb-4">
                      <Star className="w-3 h-3" strokeWidth={2} />CONSIGLIATA
                    </div>
                  )}
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${offer.tipo === "luce" ? "bg-amber-50" : "bg-orange-50"}`}>
                      {offer.tipo === "luce" ? <Zap className="w-6 h-6 text-amber-500" strokeWidth={1.5} /> : <Flame className="w-6 h-6 text-orange-500" strokeWidth={1.5} />}
                    </div>
                    <h3 className="font-bold text-lg text-gray-900">{offer.nome}</h3>
                  </div>
                  <p className="text-sm text-gray-500 mb-4">{offer.descrizione}</p>
                  <div className="mb-4">
                    <span className="text-3xl font-bold text-[#006838]">€{offer.tipo === "luce" ? offer.prezzo_kwh?.toFixed(3) : offer.prezzo_smc?.toFixed(3)}</span>
                    <span className="text-sm text-gray-500">/{offer.tipo === "luce" ? "kWh" : "Smc"}</span>
                    <p className="text-xs text-gray-400">+ €{offer.costo_fisso_mensile?.toFixed(2)}/mese</p>
                  </div>
                  {offer.vantaggi?.length > 0 && (
                    <ul className="space-y-2">
                      {offer.vantaggi.slice(0, 3).map((v, j) => (
                        <li key={j} className="flex items-center gap-2 text-sm text-gray-600">
                          <CheckCircle className="w-4 h-4 text-green-500" strokeWidth={1.5} />{v}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Final CTA */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}>
          <div className="bg-gradient-to-br from-[#ADFF2F]/30 to-[#006838]/10 rounded-[2rem] p-10 text-center border border-[#ADFF2F]/50">
            <motion.img 
              src={OTTO_URL} 
              alt="OTTO" 
              className="w-28 mx-auto mb-6"
              animate={{ y: [0, -5, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
            <h2 className="text-2xl font-bold text-[#006838] mb-3">Passa a 8energy in 5 minuti!</h2>
            <p className="text-[#006838]/80 mb-8 max-w-xl mx-auto">
              Lo switch è completamente online, gratuito e senza interruzioni di servizio.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/switch-guide">
                <Button className="rounded-full bg-[#006838] hover:bg-[#005530] text-white h-14 px-8 font-semibold shadow-lg shadow-[#006838]/20">
                  <ArrowRight className="w-5 h-5 mr-2" strokeWidth={1.5} />
                  Inizia lo Switch Online
                </Button>
              </Link>
              <Link to="/dashboard">
                <Button variant="outline" className="rounded-full border-[#006838] text-[#006838] hover:bg-[#006838]/10 h-14 px-8 font-semibold">
                  Torna alla Dashboard
                </Button>
              </Link>
            </div>
            <p className="text-sm text-[#006838]/60 mt-6">
              <Shield className="w-4 h-4 inline mr-1" strokeWidth={1.5} />
              Processo sicuro • Nessun costo nascosto • Assistenza inclusa
            </p>
          </div>
        </motion.div>

        {/* Guest User CTA - Register to Save */}
        {isGuest && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ delay: 0.7 }}
            className="mt-10"
          >
            <div className="bg-blue-50 border-2 border-blue-200 rounded-[2rem] p-8 text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Star className="w-8 h-8 text-blue-500" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Salva la tua analisi</h3>
              <p className="text-gray-600 mb-6 max-w-md mx-auto">
                Registrati gratis per salvare questa analisi, confrontarla con le prossime bollette e ricevere consigli personalizzati.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/auth">
                  <Button className="rounded-full bg-blue-600 hover:bg-blue-700 text-white h-12 px-8 font-semibold">
                    Registrati Gratis
                  </Button>
                </Link>
                <Link to="/auth">
                  <Button variant="outline" className="rounded-full border-blue-300 text-blue-600 hover:bg-blue-50 h-12 px-8 font-semibold">
                    Ho già un account
                  </Button>
                </Link>
              </div>
              <p className="text-xs text-gray-400 mt-4">
                I tuoi dati sono al sicuro. Nessuno spam, lo promettiamo!
              </p>
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
}
